import React from "react";
import { useTranslation } from "@emisgroup/acp-application-intl";
import { NoExecutionIcon } from "../ExecutionList/NoExecutionIcon";
import { NoDetails } from "../../../Common/NoDetails/NoDetails";
import { useSelections } from "../../../../hooks/useSelections";
import { memoryCache } from "../../../../service/services/memory-cache.service";
import { ExpandedRow, MultiSiteTable, RowData } from "./components/MultiSiteTable";
import { ConfigurationType, IPractice, ISiteConfiguration, IConfigSelection } from "./Types";
import { useMultiSiteTable } from "./hooks/useMultiSiteTable";

interface IMultiSiteEntryProps {
  siteList: ISiteConfiguration[];
  showSelection?: boolean;
  onRowSelected?: (selectedRows: RowData[]) => void;
  onRowClicked?: (row: RowData) => void;
}

export const MultiSiteEntry: React.FC<IMultiSiteEntryProps> = (
  { siteList, onRowSelected, onRowClicked, showSelection }) => {
  const { t: translate } = useTranslation();
  const { columns } = useMultiSiteTable();
  const [selectedRowIds, emit] = useSelections();

  const handleExpandRow = (row: RowData): ExpandedRow[] => {
    const practices = row.practices as IPractice[];
    return practices && practices.map((practice: IPractice) => ({
      id: practice.id,
      name: practice.name,
      included: practice.counts?.included,
      excluded: practice.counts?.excluded,
      population: practice.counts?.population,
    }));
  };

  const handleSelectionChange = (selectedRows: RowData[]): void => {
    const ids = selectedRows.map(row => row.id);
    memoryCache.set<IConfigSelection>("entitySelection", { ids: ids, type: ConfigurationType.SITES });
    emit(ids);
    if (onRowSelected) {
      onRowSelected(selectedRows);
    }
  };

  const isExpandable = (row: RowData): boolean => {
    const practice = row.practices as IPractice[];
    return practice && practice.length > 0;
  };

  if(!(siteList?.length > 0)) {
    return <NoDetails
      style={{ paddingTop: "10vh" }}
      title={translate(translate("MultiSiteList.NoSites.Title"))}
      description={translate("MultiSiteList.NoSites.Description")}
      notFoundIcon={ <NoExecutionIcon/> }
    />;
  }

  const mappedSiteList = siteList.map(site => ({
    ...site,
    included: site.counts && site.counts.included,
    excluded: site.counts && site.counts.excluded,
    population: site.counts && site.counts.population,
  }));

  return (
    <div className="sites-table">
      <MultiSiteTable
        columns={columns}
        rowData={mappedSiteList as unknown as RowData[]}
        isExpandable={isExpandable}
        selectedRows={selectedRowIds as string[]}
        onExpandRow={handleExpandRow}
        isSelection={showSelection || false}
        onSelectionChange={handleSelectionChange}
        onRowClicked={onRowClicked}
      />
    </div>
  );
};
